import { errorResources } from '@sunucupark/client-core';
import i18next, { type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import tr from './locales/tr.json';

export const SUPPORTED_LANGUAGES = ['tr', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];
const STORAGE_KEY = 'web.language';

/** The language the user picked, else the browser's, else Turkish. */
export function preferredLanguage(stored: string | null, browser: readonly string[]): Language {
  const candidates = [stored, ...browser].filter(
    (value): value is string => typeof value === 'string',
  );
  for (const candidate of candidates) {
    const code = candidate.slice(0, 2).toLowerCase();
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(code)) return code as Language;
  }
  // A browser asking for a language we do not have gets English: Turkish is the
  // product's first language, not a sensible guess for someone who reads neither.
  return 'en';
}

/**
 * i18next with the shared error catalogue merged in, so a gateway error code is
 * translated in one place for every client (ADR 0006).
 */
/** The initialised instance, and every bundle added on top of the built-in ones. */
let instance: I18n | null = null;
const extraBundles: Array<Readonly<Record<Language, object>>> = [];

function apply(target: I18n, bundles: Readonly<Record<Language, object>>): void {
  for (const language of SUPPORTED_LANGUAGES) {
    target.addResourceBundle(language, 'translation', bundles[language], true, true);
  }
}

/**
 * Adds strings after start-up — the mailbox does this when its chunk loads.
 * Deep-merges, so a later bundle never drops what is already there, and a
 * bundle that arrives before the instance exists is applied at initialisation
 * (which is the order a test file sees).
 */
export function addResources(bundles: Readonly<Record<Language, object>>): void {
  extraBundles.push(bundles);
  if (instance) apply(instance, bundles);
}

export function initI18n(language: Language): I18n {
  const started = i18next.use(initReactI18next);
  void started.init({
    lng: language,
    fallbackLng: 'tr',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    defaultNS: 'translation',
    resources: {
      tr: { translation: { ...tr, ...errorResources.tr } },
      en: { translation: { ...en, ...errorResources.en } },
    },
    interpolation: { escapeValue: false, defaultVariables: { product: 'Posta Merkezi' } },
    returnNull: false,
    // The mailbox adds its own strings when its chunk loads; components that are
    // already on screen have to notice (see locales/mailbox.ts).
    react: { bindI18nStore: 'added' },
  });
  instance = started;
  // `init` replaces the store, so bundles added earlier are applied again —
  // a test that re-initialises between cases keeps the strings it loaded.
  for (const bundle of extraBundles) apply(started, bundle);
  return started;
}

export function rememberLanguage(language: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // A browser with storage switched off still works; the choice just does not survive a reload.
  }
}

export function storedLanguage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export { i18next as i18n };
